
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Briefcase,
  Star,
  Shield,
  Menu,
  User,
  Sun,
  Moon,
  LogOut,
  LogIn,
  ChevronDown,
  X,
  PanelLeft,
  type LucideIcon,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useMediaQuery } from '../hooks/useMediaQuery';
import {
  HoverDropdownRoot,
  DropdownTrigger,
  DropdownContent,
  DropdownItem,
  DropdownSeparator,
} from '../components/ui/Dropdown';
import routeConfig from '../routes/routeConfig';
import { saveReturnToFromWindow } from './returnTo';
import { getNameInitials, getStoredAvatarUrl, isUsablePhotoUrl, storeAvatarUrl } from './profilePhoto';

interface UserShape {
  role: string;
  name?: string;
  profileImage?: string;
  termsAccepted?: boolean;
}

interface Props {
  user: UserShape;
  handleLogout: () => void;
  onMenuClick?: () => void;
  onFiltersClick?: (() => void) | null;
}

type RouteItem = {
  key: string;
  path?: string;
  menu?: {
    name?: string;
    path?: string;
    icon?: unknown;
    external?: boolean;
  } | null;
  children?: RouteItem[];
  roles?: string[];
};

const ICON_MAP: Record<string, LucideIcon> = {
  HomeIcon: Home,
  WorkOutline: Briefcase,
  WorkspacePremium: Star,
  AdminIcon: Shield,
};

function renderIcon(icon: unknown, size = 16): React.ReactNode {
  if (!icon) return null;

  if (typeof icon === 'string') {
    const Found = ICON_MAP[icon];
    return Found ? <Found size={size} className="shrink-0 text-muted-foreground" /> : null;
  }

  const isComponent =
    typeof icon === 'function' ||
    (typeof icon === 'object' &&
      icon !== null &&
      ('$$typeof' in icon || 'render' in icon));

  // Lucide uses `size`; MUI SvgIcon sizes via style (do not pass numeric fontSize —
  // MUI capitalize() expects 'small' | 'medium' | etc. and crashes on numbers).
  if (isComponent) {
    try {
      const Icon = icon as React.ElementType<{
        size?: number;
        className?: string;
        style?: React.CSSProperties;
      }>;
      return (
        <Icon
          size={size}
          className="shrink-0 text-muted-foreground"
          style={{ width: size, height: size, fontSize: size }}
        />
      );
    } catch {
      return null;
    }
  }

  return null;
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** ExecEdge top-nav stays highlighted on all ExecEdge routes (dropdown children + New ExecEdge). */
function isExecEdgeSectionPath(pathname: string): boolean {
  const p = pathname.toLowerCase();
  return p.startsWith('/execedge') || p.startsWith('/newexecedge');
}

const navBtnBase =
  'inline-flex items-center gap-2 rounded-full border-0 px-4 py-2.5 text-[15px] font-medium font-sans text-text transition-colors';
const navBtnIdle = 'bg-transparent hover:bg-[var(--bg-muted)] hover:text-foreground';
const navBtnActive = 'bg-accent text-primary font-semibold ring-1 ring-primary/25';

const iconBtn =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border-0 bg-transparent text-text transition-colors hover:bg-[var(--bg-muted)] hover:text-foreground';

/** Sun/Moon only — use on `group` parent for hover motion. */
const themeIconSunClass =
  'transition-transform duration-300 ease-out motion-reduce:transition-none group-hover:rotate-45 motion-reduce:group-hover:rotate-0';
const themeIconMoonClass =
  'transition-transform duration-300 ease-out motion-reduce:transition-none group-hover:-rotate-12 motion-reduce:group-hover:rotate-0';

/** Mobile logged-in: opens full nav drawer (same sheet as old hamburger). */
const mobileNavProfileTriggerClass =
  'inline-flex cursor-pointer items-center rounded-full border border-border bg-[var(--bg-subtle)] p-0.5 font-sans text-foreground transition hover:border-[var(--border-strong)] hover:bg-[var(--bg-muted)] hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

function Avatar({ src, name, size = 36 }: { src?: string; name?: string; size?: number }) {
  const initials = getNameInitials(name);

  const dim = { width: size, height: size, fontSize: Math.round(size * 0.34) };
  const urls = [src, getStoredAvatarUrl()].filter(isUsablePhotoUrl);
  const unique: string[] = [];
  for (const u of urls) {
    if (!unique.some((x) => x.split('?')[0] === u.split('?')[0])) unique.push(u);
  }
  const [idx, setIdx] = useState(0);
  const resolved = unique[Math.min(idx, Math.max(0, unique.length - 1))];

  if (resolved && idx < unique.length) {
    return (
      <img
        src={resolved}
        alt={name ?? 'Profile'}
        className="block rounded-full border-2 border-primary/30 object-cover"
        style={dim}
        onLoad={() => storeAvatarUrl(resolved)}
        onError={() => setIdx((i) => i + 1)}
      />
    );
  }

  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primaryFg"
      style={dim}
    >
      {initials}
    </div>
  );
}

const HeaderMenu = ({ user, handleLogout, onMenuClick, onFiltersClick }: Props) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const accountTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [accountMenuWidth, setAccountMenuWidth] = useState(0);
  const isHvJobsPage =
    location.pathname === '/hv/jobs' || location.pathname.startsWith('/hv/jobs/');
  const [mobileHvOpen, setMobileHvOpen] = useState(
    location.pathname.startsWith('/hv') && !isHvJobsPage,
  );
  const [mobileExecEdgeOpen, setMobileExecEdgeOpen] = useState(() =>
    isExecEdgeSectionPath(location.pathname)
  );
  const [mobileAdminOpen, setMobileAdminOpen] = useState(location.pathname.startsWith('/admin'));

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileHvOpen(location.pathname.startsWith('/hv') && !isHvJobsPage);
    setMobileExecEdgeOpen(isExecEdgeSectionPath(location.pathname));
    setMobileAdminOpen(location.pathname.startsWith('/admin'));
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const filteredMenuItems = useMemo(
    () =>
      (routeConfig as RouteItem[]).filter(
        (route) => route.menu && Array.isArray(route.roles) && route.roles.includes(user?.role)
      ),
    [user?.role]
  );

  const getMenuItems = (key: string): RouteItem[] =>
    ((routeConfig as RouteItem[]).find((route) => route.key === key && route.roles?.includes(user?.role))
      ?.children?.filter((child) => child.menu && child.roles?.includes(user?.role)) ?? []);

  const adminMenuItems = getMenuItems('admin');
  const hvMenuItems = getMenuItems('hv');
  const execedgeMenuItems = getMenuItems('execedge-menu');
  const adminNavItem = filteredMenuItems.find((item) => item.key === 'admin');

  const topBarVisible = isScrolled || mobileMenuOpen;

  const isItemActive = (item: RouteItem) => {
    if (item.key === 'hv') {
      return location.pathname.startsWith('/hv') && !isHvJobsPage;
    }
    if (item.key === 'execedge-menu') return isExecEdgeSectionPath(location.pathname);
    if (item.key === 'admin') return location.pathname.startsWith('/admin');
    if (item.menu?.external) return false;
    if (item.path === '/candidate/home') return location.pathname === '/candidate/home';
    return item.path ? location.pathname.startsWith(item.path) : false;
  };

  const isChildActive = (item: RouteItem) => {
    const targetPath = item.menu?.path ?? item.path;
    if (!targetPath) return false;
    if (targetPath === '/execedge/') return location.pathname === '/execedge' || location.pathname === '/execedge/';
    if (targetPath === '/execedge-funnel') return location.pathname.startsWith('/execedge-funnel');
    return location.pathname === targetPath || location.pathname.startsWith(`${targetPath}/`);
  };

  const handleNav = (item: RouteItem) => {
    if (item.key === 'execedge-menu' && user?.role === 'candidate') {
      navigate('/execedge/');
      return;
    }

    if (item.menu?.external && item.menu.path) {
      window.open(item.menu.path, '_blank', 'noopener,noreferrer');
      return;
    }

    if (item.path) navigate(item.path);
  };

  const handleChildNav = (item: RouteItem, parentPath?: string) => {
    const targetPath = item.menu?.path || (parentPath ? `${parentPath}${item.path ?? ''}` : item.path);
    if (!targetPath) return;
    navigate(targetPath);
  };

  const desktopNavButton = (item: RouteItem, children?: RouteItem[]) => {
    const active = isItemActive(item);
    const hasChildren = Boolean(children?.length) && !(item.key === 'execedge-menu' && user?.role === 'candidate');

    const button = (
      <button
        type="button"
        onClick={() => {
          if (!hasChildren) handleNav(item);
        }}
        className={cx(navBtnBase, active ? navBtnActive : navBtnIdle)}
      >
        <span>{item.menu?.name}</span>
        {hasChildren && <ChevronDown size={16} aria-hidden />}
      </button>
    );

    if (!hasChildren) return <div key={item.key}>{button}</div>;

    return (
      <HoverDropdownRoot key={item.key}>
        <DropdownTrigger asChild>{button}</DropdownTrigger>
        <DropdownContent align="center" sideOffset={10} minWidth={260} padded>
          {(children ?? []).map((child) => (
            <DropdownItem
              key={child.key}
              icon={renderIcon(child.menu?.icon, 15)}
              onClick={() => handleChildNav(child, item.path)}
            >
              <span className={isChildActive(child) ? 'font-semibold text-primary' : undefined}>{child.menu?.name}</span>
            </DropdownItem>
          ))}
        </DropdownContent>
      </HoverDropdownRoot>
    );
  };

  const renderAccountDropdown = () => {
    if (user?.role === 'guest') {
      return (
        <button
          type="button"
          onClick={() => {
            saveReturnToFromWindow();
            navigate('/login');
          }}
          className="cursor-pointer rounded-full border-0 bg-primary px-[18px] py-2.5 text-[15px] font-semibold text-primaryFg shadow-md transition hover:-translate-y-px hover:opacity-90 hover:shadow-lg"
        >
          Login
        </button>
      );
    }

    return (
      <HoverDropdownRoot>
        <DropdownTrigger asChild>
          <button
            type="button"
            ref={accountTriggerRef}
            className="inline-flex cursor-pointer items-center gap-2.5 rounded-full border border-border bg-[var(--bg-subtle)] py-1.5 pl-1.5 pr-2.5 font-sans text-foreground transition hover:border-[var(--border-strong)] hover:bg-[var(--bg-muted)] hover:shadow-sm"
            onMouseEnter={() => {
              const w = accountTriggerRef.current?.offsetWidth;
              if (w) setAccountMenuWidth(w);
            }}
            onPointerDown={() => {
              const w = accountTriggerRef.current?.offsetWidth;
              if (w) setAccountMenuWidth(w);
            }}
            onFocus={() => {
              const w = accountTriggerRef.current?.offsetWidth;
              if (w) setAccountMenuWidth(w);
            }}
          >
            <Avatar src={user?.profileImage || getStoredAvatarUrl()} name={user?.name} size={isMobile ? 32 : 36} />
            {!isMobile && (
              <div className="text-left leading-tight">
                <div className="text-[13px] font-semibold text-foreground">{user?.name ?? 'User'}</div>
              </div>
            )}
            {!isMobile && (
              <ChevronDown size={14} className="shrink-0 text-muted-foreground opacity-70" aria-hidden />
            )}
          </button>
        </DropdownTrigger>
        <DropdownContent
          align="end"
          sideOffset={8}
          matchTriggerWidth
          className="!border-0 !bg-transparent !p-0 !shadow-none"
          style={
            accountMenuWidth
              ? {
                  width: accountMenuWidth,
                  minWidth: accountMenuWidth,
                  maxWidth: accountMenuWidth,
                }
              : undefined
          }
        >
          <div
            className="w-full rounded-[14px] p-[1px] shadow-[var(--shadow-lg)]"
            style={{
              background:
                'linear-gradient(135deg, var(--interactive-primary) 0%, color-mix(in srgb, var(--interactive-primary) 35%, var(--success)) 48%, color-mix(in srgb, var(--interactive-primary) 20%, transparent) 100%)',
            }}
          >
            <div
              className="relative overflow-hidden rounded-[11px]"
              style={{
                background:
                  'linear-gradient(160deg, color-mix(in srgb, var(--interactive-primary) 16%, var(--bg-elevated)) 0%, var(--bg-surface) 42%, var(--bg-elevated) 100%)',
              }}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -right-6 -top-8 h-14 w-14 rounded-full opacity-50"
                style={{
                  background:
                    'radial-gradient(circle, color-mix(in srgb, var(--interactive-primary) 45%, transparent), transparent 70%)',
                }}
              />

              <div className="relative flex flex-col gap-0 p-1">
                <DropdownItem
                  onClick={() => navigate('/candidate/profile')}
                  className="group !gap-0 !rounded-lg !p-0 hover:!bg-transparent"
                >
                  <span className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 transition-all duration-200 group-hover:bg-[color-mix(in_srgb,var(--interactive-primary)_14%,transparent)]">
                    <span
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--btn-primary-label)] shadow-sm"
                      style={{
                        background:
                          'linear-gradient(145deg, var(--interactive-primary), color-mix(in srgb, var(--interactive-primary) 55%, var(--success)))',
                      }}
                    >
                      <User size={12} strokeWidth={2.4} />
                    </span>
                    <span className="pr-1 text-[12.5px] font-semibold tracking-wide text-[var(--text-primary)]">
                      Profile
                    </span>
                  </span>
                </DropdownItem>

                <DropdownItem
                  danger
                  onClick={handleLogout}
                  className="group !gap-0 !rounded-lg !p-0 hover:!bg-transparent"
                >
                  <span className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 transition-all duration-200 group-hover:bg-[color-mix(in_srgb,var(--text-danger)_12%,transparent)]">
                    <span
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--text-danger)]"
                      style={{
                        background:
                          'linear-gradient(145deg, color-mix(in srgb, var(--text-danger) 22%, var(--bg-subtle)), color-mix(in srgb, var(--text-danger) 10%, var(--bg-elevated)))',
                      }}
                    >
                      <LogOut size={12} strokeWidth={2.4} />
                    </span>
                    <span className="pr-1 text-[12.5px] font-semibold tracking-wide text-[var(--text-danger)]">
                      Log out
                    </span>
                  </span>
                </DropdownItem>
              </div>
            </div>
          </div>
        </DropdownContent>
      </HoverDropdownRoot>
    );
  };

  const renderMobileGroup = (
    label: string,
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    items: RouteItem[],
    parentPath?: string,
    sectionActive = false
  ) => {
    const hasActiveChild = sectionActive || items.some((child) => isChildActive(child));
    return (
    <div className="overflow-hidden rounded-xl border border-border bg-[var(--bg-subtle)]">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className={cx(
          'flex w-full cursor-pointer items-center justify-between border-0 bg-transparent px-[18px] py-4 text-left text-[17px] font-semibold text-text transition-colors hover:text-primary',
          (open || hasActiveChild) && 'text-primary',
          hasActiveChild && 'bg-accent'
        )}
      >
        <span>{label}</span>
        <ChevronDown
          size={18}
          className={cx('transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && (
        <div className="px-2.5 pb-3">
          {items.map((child) => (
            <button
              key={child.key}
              type="button"
              onClick={() => handleChildNav(child, parentPath)}
              className={cx(
                'flex w-full cursor-pointer items-center gap-3 rounded-lg border-0 bg-transparent px-3.5 py-3 text-left text-[15px] font-medium text-text transition-colors hover:bg-[var(--bg-muted)] hover:text-foreground',
                isChildActive(child) && 'bg-accent text-primary'
              )}
            >
              {renderIcon(child.menu?.icon, 16)}
              <span>{child.menu?.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
  };

  const primaryNavItems = filteredMenuItems.filter((item) => item.key !== 'admin');

  const headerShell = cx(
    'sticky top-0 z-[var(--z-sticky)] w-full max-w-[100vw] font-sans transition-all duration-300 supports-[backdrop-filter]:backdrop-blur-sm',
    topBarVisible
      ? 'supports-[backdrop-filter]:backdrop-blur-md border-b border-border bg-background/85 shadow-lg'
      : 'border-b border-transparent bg-transparent shadow-none'
  );

  return (
    <header className={headerShell}>
      <nav className="mx-auto w-full min-w-0 max-w-[1400px] px-4 md:px-6">
        <div className="flex h-16 min-w-0 items-center justify-between gap-3 md:gap-4">
          <button
            type="button"
            onClick={() => navigate(user?.role === 'guest' ? '/' : '/candidate/home')}
            className="flex cursor-pointer items-center border-0 bg-transparent p-0"
          >
            <span className="bg-gradient-to-r from-primary to-info bg-clip-text text-[clamp(1.5rem,2.2vw,1.875rem)] font-extrabold leading-none tracking-tight text-transparent">
              HireVeda
            </span>
          </button>

          {!isMobile && (
            <div className="flex items-center gap-2">
              {primaryNavItems.map((item) =>
                desktopNavButton(
                  item,
                  item.key === 'hv'
                    ? hvMenuItems
                    : item.key === 'execedge-menu'
                    ? execedgeMenuItems
                    : item.key === 'admin'
                    ? adminMenuItems
                    : item.children
                )
              )}
              {user?.role === 'admin' && adminNavItem && desktopNavButton(adminNavItem, adminMenuItems)}
            </div>
          )}

          <div className={cx('flex items-center', isMobile ? 'gap-2' : 'gap-3')}>
            {!isMobile && renderAccountDropdown()}

            {!isMobile && (
              <button
                type="button"
                onClick={toggleTheme}
                aria-label="Toggle theme"
                className={cx(iconBtn, 'group')}
              >
                {isDark ? (
                  <Sun size={20} className={themeIconSunClass} />
                ) : (
                  <Moon size={20} className={themeIconMoonClass} />
                )}
              </button>
            )}

            {isMobile &&
              (onFiltersClick || onMenuClick) &&
              location.pathname !== '/jobs' &&
              location.pathname !== '/jobs/' &&
              location.pathname !== '/hv/jobs' &&
              location.pathname !== '/hv/jobs/' && (
              <button
                type="button"
                onClick={onFiltersClick ?? onMenuClick}
                aria-label="Open workspace panel"
                className={iconBtn}
              >
                <PanelLeft size={20} />
              </button>
            )}

            {isMobile && user?.role !== 'guest' && (
              <button
                type="button"
                onClick={() => setMobileMenuOpen((current) => !current)}
                aria-expanded={mobileMenuOpen}
                aria-controls="header-mobile-nav"
                aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                className={cx(mobileNavProfileTriggerClass, mobileMenuOpen && 'ring-2 ring-primary/35')}
              >
                <Avatar src={user?.profileImage || getStoredAvatarUrl()} name={user?.name} size={36} />
              </button>
            )}

            {isMobile && user?.role === 'guest' && (
              <button
                type="button"
                onClick={() => setMobileMenuOpen((current) => !current)}
                aria-expanded={mobileMenuOpen}
                aria-controls="header-mobile-nav"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                className={iconBtn}
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            )}
          </div>
        </div>

        {isMobile && mobileMenuOpen && (
          <div id="header-mobile-nav" className="pb-5">
            <div className="flex flex-col gap-3 rounded-2xl border border-border bg-popover p-4 shadow-popover">
              {primaryNavItems
                .filter((item) => !item.children || item.key === 'execedge-menu')
                .map((item) => {
                  const isExecEdgeGroup = item.key === 'execedge-menu' && user?.role !== 'candidate';
                  if (isExecEdgeGroup) return null;

                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        handleNav(item);
                        setMobileMenuOpen(false);
                      }}
                      className={cx(
                        'flex w-full cursor-pointer items-center justify-between rounded-xl border-0 bg-transparent px-4 py-3.5 text-left text-[17px] font-semibold text-text transition-colors hover:bg-[var(--bg-muted)]',
                        isItemActive(item) && 'bg-accent text-primary'
                      )}
                    >
                      <span>{item.menu?.name}</span>
                    </button>
                  );
                })}

              {hvMenuItems.length > 0 && renderMobileGroup('HV', mobileHvOpen, setMobileHvOpen, hvMenuItems, '/hv')}
              {execedgeMenuItems.length > 0 && user?.role !== 'candidate' &&
                renderMobileGroup(
                  'ExecEdge',
                  mobileExecEdgeOpen,
                  setMobileExecEdgeOpen,
                  execedgeMenuItems,
                  '/execedge',
                  isExecEdgeSectionPath(location.pathname)
                )}
              {adminMenuItems.length > 0 &&
                renderMobileGroup('Admin', mobileAdminOpen, setMobileAdminOpen, adminMenuItems, '/admin')}

              <div className="mt-1 flex flex-col gap-2.5 border-t border-border pt-3">
                {user?.role !== 'guest' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/candidate/profile');
                        setMobileMenuOpen(false);
                      }}
                      className="flex w-full cursor-pointer items-center gap-3 rounded-lg border-0 bg-transparent px-3.5 py-3 text-left text-[15px] font-medium text-text transition-colors hover:bg-[var(--bg-muted)] hover:text-foreground"
                    >
                      <User size={17} />
                      <span>Profile</span>
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={toggleTheme}
                  className="group flex w-full cursor-pointer items-center gap-3 rounded-lg border-0 bg-transparent px-3.5 py-3 text-left text-[15px] font-medium text-text transition-colors hover:bg-[var(--bg-muted)] hover:text-foreground"
                >
                  {isDark ? (
                    <Sun size={17} className={themeIconSunClass} />
                  ) : (
                    <Moon size={17} className={themeIconMoonClass} />
                  )}
                  <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
                </button>

                {user?.role === 'guest' ? (
                  <button
                    type="button"
                    onClick={() => {
                      saveReturnToFromWindow();
                      navigate('/login');
                      setMobileMenuOpen(false);
                    }}
                    className="flex w-full cursor-pointer items-center justify-center gap-2.5 rounded-full border-0 bg-primary px-4 py-3.5 text-[15px] font-bold text-primaryFg shadow-md transition hover:-translate-y-px hover:opacity-90 hover:shadow-lg"
                  >
                    <LogIn size={16} />
                    <span>Login</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="flex w-full cursor-pointer items-center gap-3 rounded-lg border-0 bg-transparent px-3.5 py-3 text-left text-[15px] font-semibold text-error transition-colors hover:bg-[var(--bg-muted)]"
                  >
                    <LogOut size={17} />
                    <span>Logout</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default HeaderMenu;
